/**
 * The firm's people.
 *
 * ===========================================================================
 * ROLE, STATUS AND PLACEMENT ARE SEPARATE ACTS
 * ===========================================================================
 * Each of them changes who can open a client file, so each goes through its own
 * sub-resource where the guard, the audit entry and the session revocation happen
 * together. There is no PATCH on this resource that can reach `role_key` or `status`,
 * and there is deliberately no form on this screen that pretends otherwise.
 *
 * Changing a role additionally needs `role.edit` on top of `user.edit`. The two are
 * separate grants because they are separate decisions: editing a member's details is
 * administration, deciding what they may do is access control, and a role that can do
 * the first should not silently be able to do the second. Both are checked here for
 * rendering, and both are re-checked by the server.
 *
 * A member is never DELETED, in the sense of a row disappearing. Who held which role,
 * and when, is the resolution path for every historical authorization question, so the
 * row always stays. What the Delete action does is end the MEMBERSHIP: sessions are
 * revoked, `left_at` is stamped, and the person can no longer sign in. Suspension is
 * the reversible one; removal is not, and bringing somebody back is a fresh invitation
 * with its own start date.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type FirmMember,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  ConfirmModal,
  Dropdown,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  Input,
  Modal,
  Select,
  StatusPill,
  Table,
  Textarea,
  cn,
  useToast,
  useZodForm,
  type Column,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { Can, useCan } from '@/auth/permissions';
import { formatDateTime, humanise } from '@/lib/format';

import { useRoleCatalogue } from './useRoleCatalogue';

/**
 * Who is still at the firm. Deliberately NOT every row.
 *
 * Somebody removed under Delete keeps their membership row forever -- who held which
 * role and when is how a historical authorization question gets answered -- but an
 * administrator who has just removed a person should not still be looking at them. The
 * record surviving and the row appearing on this screen are different questions, and
 * conflating them made Delete feel like it had not worked.
 *
 * Suspension is the opposite case and stays visible on purpose: it is temporary, and
 * somebody has to remember to lift it. Hidden behind a filter, a suspended colleague is
 * simply forgotten.
 */
const PRESENT_STATUSES = ['active', 'invited', 'suspended'];

const STATUS_OPTIONS = [
  // Not "Any status": this view excludes people who have left, and a filter that hides
  // rows while claiming to show everything is worse than no filter at all.
  { value: '', label: 'Current members' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'left', label: 'Left the firm' },
];

const MEMBER_STATUS_TONES = {
  active: 'success',
  invited: 'info',
  suspended: 'warning',
  left: 'neutral',
} as const;

/**
 * One form, two ways to add somebody, chosen by `method`.
 *
 * `invite` sends them a link and they choose their own password. `credentials` is for
 * firms that hand a login over in person -- the administrator types the password and
 * the account is active immediately.
 *
 * The password is required only on the second path, which is why the check is refined
 * rather than declared: a required field that is sometimes not required reads as a bug
 * to whoever maintains it next.
 */
const inviteSchema = z
  .object({
    method: z.enum(['invite', 'credentials']),
    email: z.string().email('Enter a valid email address'),
    full_name: z.string(),
    role_key: z.string().min(1, 'Choose a role'),
    password: z.string(),
    confirm_password: z.string(),
  })
  .superRefine((values, context) => {
    if (values.method !== 'credentials') return;

    if (values.password.length < 12) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'Use at least 12 characters. A short password you chose for somebody else is the weakest one in the firm.',
      });
    }
    if (values.confirm_password !== values.password) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirm_password'],
        // Worth catching here: a mistyped password locks out a colleague who has no
        // way to tell what was actually set.
        message: 'The two passwords do not match',
      });
    }
  });

type InviteValues = z.infer<typeof inviteSchema>;

const METHOD_OPTIONS = [
  { value: 'invite', label: 'Send them an invitation' },
  { value: 'credentials', label: 'Set a password myself' },
];

export function UsersPage() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  const [changingRole, setChangingRole] = useState<FirmMember | null>(null);
  const [suspending, setSuspending] = useState<FirmMember | null>(null);
  const [removing, setRemoving] = useState<FirmMember | null>(null);
  const [reinstating, setReinstating] = useState<FirmMember | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<FirmMember>(qk.list('users', { screen: 'settings' }), STAFF.users, {
    // No explicit choice means the people still here, not literally everyone. Sent as
    // repeated `status` params, which `MemberViewSet.get_queryset` reads with getlist.
    status: status || PRESENT_STATUSES,
    search: debouncedSearch || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [status, debouncedSearch, resetCursor]);

  // The server requires BOTH capabilities for an invitation and for a role change:
  // `MemberViewSet._require_role_administration` checks `role.edit` on top of the
  // member capability. Each `useCan` is called unconditionally and combined
  // afterwards — combining them inside a `&&` would make the second call conditional.
  const canCreateUser = useCan('user', 'create');
  const canEditUser = useCan('user', 'edit');
  const canEditRole = useCan('role', 'edit');
  const canAdminister = canCreateUser && canEditRole;
  const canChangeRole = canEditUser && canEditRole;

  const columns: Column<FirmMember>[] = [
    {
      key: 'full_name',
      header: 'Name',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.full_name || '—'}</p>
          <p className="truncate text-xs text-ink-muted">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'role_name',
      header: 'Role',
      render: (row) => <Badge tone="neutral">{row.role_name}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill
            label={humanise(row.status)}
            tone={MEMBER_STATUS_TONES[row.status] ?? 'neutral'}
          />
          {!row.mfa_enabled && row.status === 'active' && (
            <Badge tone="warning" size="sm">
              No MFA
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'last_login_at',
      header: 'Last signed in',
      hideOnMobile: true,
      render: (row) => (
        <span className="text-ink-muted">
          {row.last_login_at ? formatDateTime(row.last_login_at) : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <div className="flex flex-wrap items-center justify-end gap-1">
          {canChangeRole && (
            <Button variant="ghost" size="sm" onClick={() => setChangingRole(row)}>
              Change role
            </Button>
          )}
          <Can module="user" action="edit">
            <Dropdown
              align="end"
              label={`Actions for ${row.full_name || row.email}`}
              items={[
                row.status === 'suspended'
                  ? { key: 'reinstate', label: 'Reinstate', onSelect: () => setReinstating(row) }
                  : {
                      key: 'suspend',
                      label: 'Suspend',
                      disabled: row.status === 'left',
                      onSelect: () => setSuspending(row),
                    },
                {
                  key: 'remove',
                  label: 'Delete',
                  // Destructive tone and a divider, so the irreversible item is never
                  // the one the pointer lands on by accident.
                  destructive: true,
                  separatorBefore: true,
                  disabled: row.status === 'left',
                  onSelect: () => setRemoving(row),
                },
              ]}
              trigger={({ open }) => (
                <span
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded px-3',
                    'text-sm font-medium shadow-sm transition-colors',
                    'border border-line-strong bg-surface text-ink hover:bg-surface-sunken',
                    open && 'bg-surface-sunken',
                  )}
                >
                  Actions
                  <span aria-hidden="true" className="text-xs text-ink-muted">
                    ▾
                  </span>
                </span>
              )}
            />
          </Can>
        </div>
      ),
    },
  ];

  return (
    <>
      {/*
        This screen is the firm's OWN people and nothing else. There is no client role
        in the list below, because a client is not a member of the firm: they hold a
        different kind of credential, on a different audience, with no membership row
        and no role at all.

        Said out loud because the absence is otherwise indistinguishable from a missing
        feature. An administrator looking for "how do I give my client a login" comes
        here first — it is the screen called Users — finds no client anywhere in it, and
        has no way of telling whether they are looking in the wrong place or whether the
        product cannot do it.
      */}
      <p className="mb-3 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
        These are the people who work at the firm. Clients do not appear here and are
        not given a role — a client’s access to their own portal is granted on their
        record, under <span className="text-ink">Portal access</span>, one matter at a
        time.
      </p>

      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:max-w-lg">
          <Input
            label="Search"
            hideLabel
            type="search"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            label="Status"
            hideLabel
            options={STATUS_OPTIONS}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          />
        </div>
        {canAdminister && (
          <Button variant="primary" onClick={() => setInviting(true)}>
            Add member
          </Button>
        )}
      </div>

      <Table
        caption="Firm members"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No members to show"
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />

      <InviteDialog open={inviting} onClose={() => setInviting(false)} />
      <RoleDialog member={changingRole} onClose={() => setChangingRole(null)} />
      <SuspendDialog member={suspending} onClose={() => setSuspending(null)} />
      <RemoveDialog member={removing} onClose={() => setRemoving(null)} />
      <ReinstateDialog member={reinstating} onClose={() => setReinstating(null)} />
    </>
  );
}

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const { toast } = useToast();
  const roles = useRoleCatalogue();

  const form = useZodForm(inviteSchema, {
    defaultValues: {
      method: 'invite',
      email: '',
      full_name: '',
      role_key: '',
      password: '',
      confirm_password: '',
    },
  });

  const method = form.watch('method');
  const settingPassword = method === 'credentials';

  const invite = useApiMutation<FirmMember, InviteValues>(
    {
      mutationFn: (values) =>
        api.post<FirmMember>(`${STAFF.users}/`, {
          email: values.email,
          full_name: values.full_name || undefined,
          role_key: values.role_key,
          // Its presence is what picks the path on the server too. Omitted entirely
          // rather than sent empty, so an invitation never carries a password field.
          ...(values.method === 'credentials' ? { password: values.password } : {}),
        }),
      invalidates: [qk.all('users')],
    },
    {
      onSuccess: (member) => {
        const byHand = form.getValues('method') === 'credentials';
        toast({
          title: byHand ? 'Member added' : 'Invitation sent',
          description: byHand
            ? `${member.email} can sign in now as ${member.role_name}. Give them the password in person, not by email.`
            : `${member.email} has been invited as ${member.role_name}.`,
          tone: 'success',
        });
        form.reset();
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'That member could not be added',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The member could not be invited. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a member"
      description="They join with the role you choose here, and hold no permissions until they have signed in."
    >
      <Form
        form={form}
        ariaLabel="Invite a member"
        error={invite.error}
        onSubmit={(values) => invite.mutateAsync(values)}
      >
        <FormSelect
          form={form}
          name="method"
          label="How they get in"
          options={METHOD_OPTIONS}
          hint={
            settingPassword
              ? 'The account works immediately. Only for an address that has never signed in here before.'
              : 'They follow a link and choose their own password.'
          }
        />

        <FormInput form={form} name="email" label="Email" type="email" autoFocus />
        <FormInput form={form} name="full_name" label="Name" />
        <FormSelect
          form={form}
          name="role_key"
          label="Role"
          placeholder="Choose a role"
          options={roles.options}
          hint="What they can do, everywhere in the firm. See the Roles section for what each one holds."
        />

        {settingPassword && (
          <>
            <FormInput
              form={form}
              name="password"
              label="Password"
              type="password"
              autoComplete="new-password"
              hint="You will know this password. Ask them to change it once they are in."
            />
            <FormInput
              form={form}
              name="confirm_password"
              label="Password again"
              type="password"
              autoComplete="new-password"
            />
          </>
        )}

        <FormActions
          submitLabel={settingPassword ? 'Create the account' : 'Send invitation'}
          busy={invite.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}

function RoleDialog({
  member,
  onClose,
}: {
  member: FirmMember | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const roles = useRoleCatalogue();
  const [roleKey, setRoleKey] = useState('');

  useEffect(() => {
    setRoleKey(member?.role_key ?? '');
  }, [member]);

  const change = useApiMutation<FirmMember, string>(
    {
      mutationFn: (key) =>
        api.post<FirmMember>(`${STAFF.userRole(member?.id ?? '')}/`, { role_key: key }),
      invalidates: [qk.all('users')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Role changed',
          description: `${updated.full_name || updated.email} is now ${updated.role_name}.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The role change was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The role could not be changed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  const selected = roles.byKey.get(roleKey);

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      title="Change role"
      description={member ? `${member.full_name || member.email} is currently ${member.role_name}.` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={change.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!roleKey || roleKey === member?.role_key}
            loading={change.isPending}
            onClick={() => change.mutate(roleKey)}
          >
            Change role
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="New role"
          options={roles.options}
          placeholder="Choose a role"
          value={roleKey}
          onChange={(event) => setRoleKey(event.target.value)}
        />
        {/* An administrator choosing a role is making an access-control decision, so
            what it confers is shown rather than left to be looked up. */}
        {selected && (
          <div className="rounded border border-line bg-surface-sunken p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              {selected.name} holds
            </p>
            <ul className="mt-1.5 flex flex-wrap gap-1">
              {selected.permission_keys.map((key) => (
                <li key={key}>
                  <Badge tone="neutral" size="sm">
                    {key}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-sm text-ink-muted">
          This changes what they can see and do everywhere in the firm, and takes effect
          on their next request.
        </p>
      </div>
    </Modal>
  );
}

function RemoveDialog({
  member,
  onClose,
}: {
  member: FirmMember | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [member]);

  const remove = useApiMutation<FirmMember, void>(
    {
      mutationFn: () =>
        api.post<FirmMember>(`${STAFF.userRemove(member?.id ?? '')}/`, {
          reason: reason || undefined,
        }),
      invalidates: [qk.all('users')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Member removed',
          description: `${updated.full_name || updated.email} has left the firm.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The removal was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The member could not be removed. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      title="Remove this member from the firm"
      description={member ? `${member.full_name || member.email} · ${member.role_name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink">
          Their sessions end immediately and they can no longer sign in. Unlike a suspension
          this is not reversible: to bring them back you invite them again, and the new
          membership starts from that date rather than pretending they never left.
        </p>
        <p className="text-sm text-ink-muted">
          The record itself is kept. Who held which role, and when, stays answerable — a
          matter closed years from now must still be able to say who approved it.
        </p>
        <Textarea
          label="Reason"
          hint="Recorded in the audit trail. Optional, but it is what somebody reads when they ask why."
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function SuspendDialog({
  member,
  onClose,
}: {
  member: FirmMember | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [member]);

  const suspend = useApiMutation<FirmMember, void>(
    {
      mutationFn: () =>
        api.post<FirmMember>(`${STAFF.userSuspend(member?.id ?? '')}/`, {
          reason: reason || undefined,
        }),
      invalidates: [qk.all('users')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Member suspended',
          description: `${updated.full_name || updated.email} can no longer sign in.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The suspension was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The member could not be suspended. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      title="Suspend this member"
      description={member ? `${member.full_name || member.email} · ${member.role_name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={suspend.isPending}>
            Cancel
          </Button>
          <Button variant="danger" loading={suspend.isPending} onClick={() => suspend.mutate()}>
            Suspend
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink">
          Their sessions end and they can no longer sign in. The membership row stays, so
          who held which role and when is still answerable. Reversible: reinstate brings
          them back. Removal, under Delete, does not.
        </p>
        <Textarea
          label="Reason"
          hint="Recorded in the audit trail. Optional, but it is what somebody reads when they ask why."
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}

function ReinstateDialog({
  member,
  onClose,
}: {
  member: FirmMember | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const reinstate = useApiMutation<FirmMember, void>(
    {
      mutationFn: () => api.post<FirmMember>(`${STAFF.userReinstate(member?.id ?? '')}/`),
      invalidates: [qk.all('users')],
    },
    {
      onSuccess: (updated) => {
        toast({
          title: 'Member reinstated',
          description: `${updated.full_name || updated.email} can sign in again as ${updated.role_name}.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The reinstatement was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The member could not be reinstated. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <ConfirmModal
      open={member !== null}
      onClose={onClose}
      title="Reinstate this member"
      description={member ? `${member.full_name || member.email} · ${member.role_name}` : ''}
      consequences="They can sign in again and hold their role's permissions from their next request. Their role is unchanged by this."
      confirmLabel="Reinstate"
      loading={reinstate.isPending}
      onConfirm={() => reinstate.mutate()}
    />
  );
}
